import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './ProstayCacCalculatorCost.css'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LabelList } from "recharts";
import RefreshIcon from '../icons/RefreshIcon';

// --- Utility helpers ---
const currencySymbols = {
    USD: "$",
    EUR: "€",
    GBP: "£",
    IDR: "Rp",
    JPY: "¥",
    AUD: "A$",
    SGD: "S$",
    RON: "lei"
};

//currencies
const currencies = ["USD", "EUR", "GBP", "RON", "JPY", "AUD", "SGD", "CAD", "CHF", "IDR"];


const formatMoney = (value, currency) => {
    try {
        return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(value);
    } catch {
        const sym = currencySymbols[currency] || "";
        return sym + value.toLocaleString();
    }
};

// Canale default
const defaultChannels = [
    { key: "paid_ads", label: "Paid Ads", expense: 0, customers: 0 },
    { key: "otas", label: "OTAs", expense: 0, customers: 0 },
    { key: "social", label: "Social Media", expense: 0, customers: 0 },
    { key: "direct", label: "Direct Website", expense: 0, customers: 0 },
    { key: "agents", label: "Travel Agents", expense: 0, customers: 0 },
];

export default function CACCalculator() {
    const { t } = useTranslation();
    const [currency, setCurrency] = useState("USD");
    const [totalExpense, setTotalExpense] = useState("");
    const [newCustomers, setNewCustomers] = useState("");
    const [showAdvanced, setShowAdvanced] = useState(true);
    const [clv, setClv] = useState("");
    const [enterByChannel, setEnterByChannel] = useState(false);
    const [channels, setChannels] = useState(() => defaultChannels);
    const [activeTab, setActiveTab] = useState("summary");

    const channelTotals = useMemo(() => {
        const totalE = channels.reduce((s, c) => s + (Number(c.expense) || 0), 0);
        const totalC = channels.reduce((s, c) => s + (Number(c.customers) || 0), 0);
        return { totalE, totalC };
    }, [channels]);

    const effectiveExpense = enterByChannel ? channelTotals.totalE : Number(totalExpense) || 0;
    const effectiveCustomers = enterByChannel ? channelTotals.totalC : Number(newCustomers) || 0;

    const cac = useMemo(() => {
        if (!effectiveCustomers || effectiveCustomers <= 0) return 0;
        return effectiveExpense / effectiveCustomers;
    }, [effectiveExpense, effectiveCustomers]);

    const ratioCLVtoCAC = useMemo(() => {
        const clvNum = Number(clv) || 0;
        if (!clvNum || !cac) return 0;
        return clvNum / cac;
    }, [clv, cac]);

    const ratioText = useMemo(() => {
        if (!ratioCLVtoCAC) return "—";
        return `${ratioCLVtoCAC.toFixed(2)} : 1`;
    }, [ratioCLVtoCAC]);

    const healthy = ratioCLVtoCAC >= 3;

    const barData = useMemo(() => {
        if (!cac && !(Number(clv) || 0)) return [];
        const clvVal = Number(clv) || 0;
        return [
            { name: "CAC", value: Number(cac.toFixed(2)) },
            ...(clvVal ? [{ name: "CLV", value: Number(clvVal.toFixed(2)) }] : []),
        ];
    }, [cac, clv]);

    const pieData = useMemo(() => {
        if (!enterByChannel) return [];
        const total = channelTotals.totalE || 1;
        return channels
            .filter((c) => (Number(c.expense) || 0) > 0)
            .map((c) => ({ name: c.label, value: Number(c.expense) || 0, pct: ((Number(c.expense) || 0) / total) * 100 }));
    }, [channels, channelTotals.totalE, enterByChannel]);

    const COLORS = ["#1643E0", "#4062F6", "#5A79FF", "#8FA7FF", "#BECBFF"];

    const resetAll = () => {
        setCurrency("USD");
        setTotalExpense("");
        setNewCustomers("");
        setShowAdvanced(true);
        setClv("");
        setEnterByChannel(false);
        setChannels(defaultChannels.map((c) => ({ ...c, expense: 0, customers: 0 })));
        setActiveTab("summary");
    };

    return (
        <>
            <div className="prostaycac-calculator" style={{ maxWidth: '1200px', margin: '0 auto' }}>
                {/* Header */}
                <div className="prostay-card">
                    <div className="row mb-4">
                        <div className="col-lg-9">
                            <h3 className="mb-1">{t('Customer Acquisition Cost (CAC) Calculator')}</h3>
                            <p className="">
                                {t('Calculate your hotel’s Customer Acquisition Cost. Enter total sales & marketing spend and new customers for a period. Optional: break it down by channel and compare CAC against Customer Lifetime Value (CLV).')}
                            </p>
                        </div>
                        <div className="col-lg-3 d-flex justify-content-end">
                            <button 
                                className="transparent-btn gap-2 d-flex align-items-center"
                                style={{height:'fit-content'}}
                                onClick={resetAll}
                            >
                                <RefreshIcon/>
                                {t('Reset')}
                            </button>
                        </div>
                    </div>
                    <div className="card-body-border p-4">
                        {/* Conf */}
                        <div className="row mb-4">
                            <div className="col-md-4 d-flex flex-column">
                                <label className="">{t('Currency')}</label>
                                <select
                                    value={currency}
                                    onChange={(e) => setCurrency(e.target.value)}
                                >
                                    {currencies.map(c => (
                                        <option key={c} value={c}>{c}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="col-md-4 d-flex align-items-end">
                                <div className="form-check form-switch">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="showAdvanced"
                                        checked={showAdvanced}
                                        onChange={(e) => setShowAdvanced(e.target.checked)}
                                    />
                                    <label htmlFor="showAdvanced">
                                        {t('Show advanced options')}
                                    </label>
                                </div>
                            </div>

                            <div className="col-md-4 d-flex align-items-end">
                                <div className="form-check form-switch">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="byChannel"
                                        checked={enterByChannel}
                                        onChange={(e) => setEnterByChannel(e.target.checked)}
                                    />
                                    <label htmlFor="byChannel">
                                        {t('Enter expenses & customers by channel')}
                                    </label>
                                </div>
                            </div>
                        </div>



                        {/* Inputs */}
                        {!enterByChannel ? (
                            <div className="row mb-4">
                                <div className="col-md-6">
                                    <label>
                                        {t('Total sales & marketing expenses')} ({currency})
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder={t("e.g., 15000")}
                                        value={totalExpense}
                                        onChange={(e) => setTotalExpense(e.target.value.replace(/[^0-9.]/g, ""))}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label>
                                        {t('New customers acquired (same period)')}
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder={t('e.g, 200')}
                                        value={newCustomers}
                                        onChange={(e) => setNewCustomers(e.target.value.replace(/\D/g, ""))}
                                    />
                                </div>
                            </div>
                        )
                            :
                            (
                                <div style={{ marginBlock: "20px" }}>
                                    <label className='mb-2'>{t('Enter expense and customers per channel; totals are calculated automatically.')}</label>
                                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                        <thead>
                                            <tr>
                                                <th style={{ border: "1px solid #D5D7DD", textAlign: "left", padding: "8px" }}>{t('Channel')}</th>
                                                <th style={{ border: "1px solid #D5D7DD", textAlign: "left", padding: "8px" }}>{t('Expense')} ({currency})</th>
                                                <th style={{ border: "1px solid #D5D7DD", textAlign: "left", padding: "8px" }}>{t('New customers')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {channels.map((ch, idx) => (
                                                <tr key={ch.key}>
                                                    <td style={{ borderInline: '1px solid #D5D7DD', borderBottom: '1px solid #D5D7DD', padding: "8px" }}>{ch.label}</td>
                                                    <td style={{ borderInline: '1px solid #D5D7DD', borderBottom: '1px solid #D5D7DD', padding: "8px" }}>
                                                        <input style={{ width: "100%", padding: "4px", borderRadius: '6px' }} value={String(ch.expense)} onChange={(e) => {
                                                            const v = e.target.value.replace(/[^0-9.]/g, "");
                                                            setChannels((prev) => prev.map((c, i) => i === idx ? { ...c, expense: v === "" ? 0 : Number(v) } : c));
                                                        }} />
                                                    </td>
                                                    <td style={{ borderInline: '1px solid #D5D7DD', borderBottom: '1px solid #D5D7DD', padding: "8px" }}>
                                                        <input style={{ width: "100%", padding: "4px", borderRadius: '6px' }} value={String(ch.customers)} onChange={(e) => {
                                                            const v = e.target.value.replace(/\D/g, "");
                                                            setChannels((prev) => prev.map((c, i) => i === idx ? { ...c, customers: v === "" ? 0 : Number(v) } : c));
                                                        }} />
                                                    </td>
                                                </tr>
                                            ))}
                                            <tr>
                                                <td style={{ borderInline: '1px solid #D5D7DD', borderBottom: '1px solid #D5D7DD', borderTop: '2px solid black', padding: "6px", fontWeight: "bold" }}>Totals</td>
                                                <td style={{ borderInline: '1px solid #D5D7DD', borderBottom: '1px solid #D5D7DD', borderTop: '2px solid black', padding: "6px", fontWeight: "bold" }}>{formatMoney(channelTotals.totalE, currency)}</td>
                                                <td style={{ borderInline: '1px solid #D5D7DD', borderBottom: '1px solid #D5D7DD', borderTop: '2px solid black', padding: "6px", fontWeight: "bold" }}>{channelTotals.totalC.toLocaleString()}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}

                        {/* CLV Input */}
                        {showAdvanced && (
                            <div className="row mb-4">
                                <div className="col-md-4">
                                    <label>
                                        {t('Customer LifeTime Value (CLV) - optional')} ({currency})
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder={t("e.g., 200")}
                                        value={clv}
                                        onChange={(e) => setClv(e.target.value.replace(/[^0-9.]/g, ""))}
                                    />
                                    <small>{t('Use the 3:1 CLV:CAC rule of thumb for healty growth.')}</small>
                                </div>
                            </div>
                        )}

                        {/* Results */}
                        <div className="row mb-4">
                            <div className="col-lg-4 mb-3">
                                <div className="card h-100">
                                    <div className="card-body custom-label">
                                        <label>{t('Customer Acquisition Cost')}</label>
                                        <h6 className="my-2">{formatMoney(cac || 0, currency)}</h6>
                                        <small>
                                            {effectiveCustomers > 0
                                                ? `${formatMoney(effectiveExpense, currency)} ÷ ${effectiveCustomers.toLocaleString()} ${t('clients')}`
                                                : `${t('Enter expense and customers to calculate')}`
                                            }
                                        </small>
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-4 mb-3">
                                <div className="card h-100">
                                    <div className="card-body custom-label">
                                        <label>{t('CLV : CAC Ratio')}</label>
                                        <h6 className={`my-2 ${healthy ? 'text-success-custom' : 'text-warning-custom'}`}>
                                            {ratioText}
                                        </h6>
                                        <small>
                                            {ratioCLVtoCAC
                                                ? (healthy ? `${t('Healthy')}: ≥ 3:1` : `${t('Below target: aim for')} ≥ 3:1`)
                                                : `${t('Enter CLV to see the ratio')}`
                                            }
                                        </small>
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-4 mb-3">
                                <div className="card h-100">
                                    <div className="card-body custom-label">
                                        <label className="mb-2">{t('Period Totals')}</label>
                                        <div className="my-1">
                                            <small style={{ fontSize: '14px', color: '#101828' }} className="h5">{formatMoney(effectiveExpense, currency)}</small>
                                            <small style={{ fontSize: '14px' }} className="text-muted ms-2">{t('expenses')}</small>
                                        </div>
                                        <div>
                                            <small className="h5">{effectiveCustomers.toLocaleString()}</small>
                                            <small className="text-muted ms-2">{t('new customers')}</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Charts with Tabs */}
                        <div style={{ marginTop: "20px" }}>
                            <div style={{ display: "flex" }} className='gray-background'>
                                <button onClick={() => setActiveTab("summary")} className={activeTab === 'summary' ? 'transparent-btn' : 'transparent-white-btn'} >{t('CAC vs CLV')}</button>
                                <button onClick={() => setActiveTab("channels")} disabled={!enterByChannel || pieData.length === 0} className={activeTab === 'channels' ? 'transparent-btn' : 'transparent-white-btn'}>{t('Channel Breakdown')}</button>
                            </div>

                            {
                                barData.length > 0 || pieData.length > 0 ?
                                    <div>
                                        {activeTab === "summary" && barData.length > 0 && (
                                            <div style={{ width: "100%", height: "300px", marginTop: "20px" }}>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart data={barData} margin={{ top: 30, right: 20, left: 0, bottom: 0 }}>
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                                                        <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => formatMoney(Number(v), currency)} />
                                                        <RTooltip formatter={(v) => formatMoney(Number(v), currency)} />
                                                        <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                                                            <LabelList dataKey="value" position="top" formatter={(v) => formatMoney(Number(v), currency)} />
                                                            {barData.map((_, i) => (
                                                                <Cell key={`c-${i}`} fill={COLORS[i % COLORS.length]} />
                                                            ))}
                                                        </Bar>
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        )}
                                        {activeTab === "channels" && enterByChannel && pieData.length > 0 && (
                                            <div style={{ width: "100%", height: "300px", marginTop: "20px" }}>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <PieChart>
                                                        <RTooltip formatter={(v, n, p) => [formatMoney(Number(v), currency), `${p?.payload?.name}`]} />
                                                        <Legend />
                                                        <Pie data={pieData} nameKey="name" dataKey="value" innerRadius={60} outerRadius={90} paddingAngle={2} label={(entry) => `${entry.name}: ${entry.pct.toFixed(1)}%`}>
                                                            {pieData.map((_, i) => (
                                                                <Cell key={`p-${i}`} fill={COLORS[i % COLORS.length]} />
                                                            ))}
                                                        </Pie>
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            </div>
                                        )}
                                    </div>
                                    :
                                    <div className='d-flex align-items-center justify-content-center'>
                                        <p className='py-4'>
                                            {t('Enter the data to see the CAC vs CLV chart')}
                                        </p>
                                    </div>
                            }



                        </div>
                        {/* Tips */}
                        <div className="tips-section p-4">
                            <label>
                                {t('Tips to reduce CAC')}
                            </label>
                            <ul className="mb-0">
                                <li className="my-2">{t('Shift budget toward high-ROI channels (e.g., direct website, returning guests).')}</li>
                                <li className="mb-2">{t('Improve conversion on your booking engine with simpler checkout and clear value props.')}</li>
                                <li className="mb-2">{t('Leverage loyalty, email, and remarketing to increase repeat bookings.')}</li>
                                <li className="mb-0">{t('Align sales & marketing with seasonal demand using rate and promo experiments.')}</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}